import * as React from "react"
import JobForm from "./JobFormContext"
import DatePicker from "react-datepicker"
import Select from "react-select"
import moment from "moment"
import Datetime from "react-datetime"
import "react-datetime/css/react-datetime.css";

const EXPERIENCE_OPTIONS = [
  { value: "0-2", label: "0-2 Years" },
  { value: "2-5", label: "2-5 Years" },
  { value: "5+", label: "5+ Years" },
];

// The only hiring types the AI Match sweep scores, and so the only ones that
// carry an automatic-invite setting (Job#ai_candidate_match_eligible?).
export const AUTO_INVITE_POSITION_TYPES = ["fulltime", "parttime"];

export default function GeneralInfo({
  practices,
  job_types,
  position_types,
  tabIndexFunc,
  validation,
  setValidation,
  temp = false,
  cancelButton
}) {
  const [jobState, setJobState] = React.useContext(JobForm)
  const [confidentialState, setConfidentialState] = React.useState(false)

  //this state makes sure error messages dont show unless someone is trying to procceed without filing out sections
  const [errorMessage, setErrorMessage] = React.useState(false)

  const blank = (v) => v === null || v === undefined || v === ""
  // Per-field error flags — each incomplete field carries its own message
  // instead of one aggregate banner.
  const positionError = errorMessage && blank(jobState.job_type_id)
  const locationError = errorMessage && blank(jobState.practice_id)
  const hiringTypeError = errorMessage && blank(jobState.position_type)
  // Temp jobs gate on four more fields. Without their own messages the user
  // just gets a dead Continue button and an aggregate banner that never names
  // the empty field — legacy temp jobs with no saved mobile number were an
  // unrecoverable dead end.
  const isTemp = jobState.position_type === "temp"
  const smsPhoneError = errorMessage && isTemp && blank(jobState.sms_phone)
  const tempDateError = errorMessage && isTemp && blank(jobState.temp_date)
  const tempStartError = errorMessage && isTemp && blank(jobState.temp_start)
  const tempEndError = errorMessage && isTemp && blank(jobState.temp_end)

  const updateJobState = (key, value) => setJobState(currentJobState => ({ ...currentJobState, [`${key}`]: value }))

  const selectConfidential = () => {
    setConfidentialState(!confidentialState)
    setJobState({ ...jobState, confidential: !confidentialState })
  }

  // Automatic invites are on unless the poster turns them off, and only
  // permanent listings are scored, so the switch is read straight off the job
  // state with `undefined` meaning "on" (a brand new job seeds true in
  // Form.tsx, but an older cached payload without the key must still read as
  // on rather than silently opting the job out).
  const autoInviteOn = jobState.auto_invite_matches !== false
  const autoInviteEligible = AUTO_INVITE_POSITION_TYPES.includes(jobState.position_type)

  const selectAutoInvite = () =>
    setJobState(currentJobState => ({
      ...currentJobState,
      auto_invite_matches: currentJobState.auto_invite_matches === false
    }))

  //this is the validation that prevents using the continue button
  const validationGI = () => {
    const {
      position_type,
      job_type_id,
      practice_id,
      sms_phone,
      temp_date,
      temp_start,
      temp_end,
    } = jobState

    setErrorMessage(true)

    const position_attributes_valid = position_type !== null && position_type !== "" &&
      job_type_id !== null && job_type_id !== "" &&
      practice_id !== null && practice_id !== ""

    const temp_attributes_valid = sms_phone !== null && sms_phone !== "" &&
      temp_date !== null && temp_date !== "" &&
      temp_start !== null && temp_start !== "" &&
      temp_end !== null && temp_end !== ""

    if (position_attributes_valid) {
      if (jobState.position_type !== "temp") {
        tabIndexFunc(1)
      } else {
        //these are the checks for the temp position
        if (temp_attributes_valid) {
          tabIndexFunc(1)
        }
      }
    }
  }

  //These three use effects listen to a change in date or time and will update the job state
  const tempDateHandler = e => setJobState(currentJobState => ({ ...currentJobState, ["temp_date"]: e }))
  const startDateHandler = e => setJobState(currentJobState => ({ ...currentJobState, ["start_date"]: e }))
  const startTimeHandler = e => {
    // e is a moment object from react-datetime when picked, but a string when typed
    const m = moment.isMoment(e) ? e : moment(e, ['h:mm A', 'HH:mm']);
    if (!m.isValid()) return;

    const utcDate = moment.utc()
      .hour(m.hour())
      .minute(m.minute())
      .second(0)
      .millisecond(0)
      .toDate();

    setJobState(currentJobState => ({
      ...currentJobState,
      ["temp_start"]: utcDate,
      ["temp_start_readable"]: null
    }));
  }
  const endTimeHandler = e => {
    // e is a moment object from react-datetime when picked, but a string when typed
    const m = moment.isMoment(e) ? e : moment(e, ['h:mm A', 'HH:mm']);
    if (!m.isValid()) return;

    const utcDate = moment.utc()
      .hour(m.hour())
      .minute(m.minute())
      .second(0)
      .millisecond(0)
      .toDate();

    setJobState(currentJobState => ({
      ...currentJobState,
      ["temp_end"]: utcDate,
      ["temp_end_readable"]: null
    }));
  }

  //Listens to see if a person clicks off temp as a position type and will clear the values.
  // Prevents someone from choosing temp then changing mind
  React.useEffect(() => {
    if (jobState.position_type !== "temp") {
      setJobState(currentJobState => ({
        ...currentJobState,
        ["temp_date"]: null,
        ["temp_start"]: null,
        ["temp_end"]: null
      }))
    }

    if (jobState.position_type === "temp") {
      setConfidentialState(false)
      setJobState(currentJobState => ({
        ...currentJobState,
        ["confidential"]: false
      }))

      // Only set default times for new jobs, not when editing existing ones
      if (!jobState.id) {
        startTimeHandler(moment().utc().hour(8).minute(0).second(0).millisecond(0))
        endTimeHandler(moment().utc().hour(17).minute(0).second(0).millisecond(0))
      }

    }

    // Only fulltime/parttime are scored, so only those show the switch (see
    // autoInviteEligible below). We used to delete the key here when the
    // hiring type moved off a scored type and re-seed it as `currentValue ??
    // true` when it came back, but that silently flipped a saved false back
    // to true on a fulltime -> temp -> fulltime round trip. The server
    // already ignores this value for non-eligible types, so there is no
    // need to touch it while the type is ineligible: just leave whatever is
    // there alone and seed true only the first time an eligible type sees
    // no value at all.
    setJobState(currentJobState => {
      if (!AUTO_INVITE_POSITION_TYPES.includes(currentJobState.position_type)) return currentJobState
      if (currentJobState.auto_invite_matches !== undefined) return currentJobState

      return { ...currentJobState, auto_invite_matches: true }
    })
  }, [jobState.position_type])

  const customStyles = {

    option: (styles, { isFocused }) => {
      return {
        ...styles,
        backgroundColor: isFocused ? '#0275d8' : 'white',
        color: isFocused ? 'white' : 'black',
        cursor: 'pointer',
        padding: '10px',
        ':hover': {
          backgroundColor: '#0275d8',
          color: 'white',
        }

      }
    },
    control: (styles) => ({
      ...styles,
      backgroundColor: 'white',
      minHeight: '48px',
      borderColor: '#ccd8e5',
      borderRadius: '0.6rem',
      boxShadow: 'none',
      ':hover': {
        borderColor: '#0275d8'
      },
      ':focus': {
        borderColor: '#0275d8',
        boxShadow: '0 0 0 0.2rem rgba(2, 117, 216, 0.25)'
      }
    }),
    singleValue: (styles) => ({
      ...styles,
      color: '#333',
    }),
    menu: (styles) => ({
      ...styles,
      boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)',
      borderRadius: '0.25rem',

    }),
  }

  // Create job type options for Select
  const jobTypeOptions = job_types.map(position_type => ({
    value: position_type.id,
    label: position_type.title
  }))

  // Selected job type option
  const selectedJobType = jobTypeOptions.find(option => option.value.toString() === jobState.job_type_id?.toString())

  // Create position type options for Select
  const positionTypeOptions = position_types.map(position_type => ({
    value: position_type.shortname,
    label: position_type.name
  }))

  // Selected position type option
  const selectedPositionType = positionTypeOptions.find(option => option.value === jobState.position_type)

  return (
    <div className="job-builder-step job-builder-step--details">
      <div className={`form-group py-2${positionError ? ' has-error' : ''}`}>
        <h4 className="font-weight-bold">Position</h4>
        <p className="job-builder-field-help">Choose the dental role candidates will see in search.</p>
        <Select
          styles={customStyles}
          options={jobTypeOptions}
          value={selectedJobType}
          onChange={(selected) => updateJobState("job_type_id", selected.value)}
          placeholder="Select a position"
          classNamePrefix="react-select"
          isClearable={false}
        />
        {positionError && (
          <p className="job-builder-field-error" role="alert">Select a position.</p>
        )}
      </div>

      <div className={`form-group py-2${locationError ? ' has-error' : ''}`}>
        <h4 className="font-weight-bold">Location</h4>
        <p className="job-builder-field-help">Select the practice where this person will work.</p>
        <Select
          styles={{
            ...customStyles,
            option: (styles, { isFocused }) => ({
              ...styles,
              backgroundColor: isFocused ? '#0275d8' : 'white',
              color: isFocused ? 'white' : 'black',
              cursor: 'pointer',
              padding: '10px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              ':hover': {
                backgroundColor: '#0275d8',
                color: 'white',
              }
            }),
            singleValue: (styles) => ({
              ...styles,
              color: 'inherit',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
            })
          }}
          options={practices.map(practice => ({
            value: practice.id,
            label: (
              <div className="d-flex align-items-center">
                <div className="location-avatar me-2">
                  {practice.avatar_url ? (
                    <img
                      src={practice.avatar_url}
                      alt={practice.name}
                      className="rounded-circle"
                      style={{ width: '32px', height: '32px', objectFit: 'cover' }}
                    />
                  ) : (
                    <div
                      className="rounded-circle bg-primary d-flex justify-content-center align-items-center"
                      style={{ width: '32px', height: '32px' }}
                    >
                      <span style={{ color: 'white', fontSize: '14px' }}>{practice.name?.charAt(0).toUpperCase()}</span>
                    </div>
                  )}
                </div>
                <div className="location-details">
                  <div className="location-name fw-bold">{practice.name}</div>
                  <div className="location-address" style={{ color: 'inherit', fontSize: '0.8rem' }}>
                    {practice.street_address}, {practice.city}, {practice.state}, {practice.zip}
                  </div>
                </div>
              </div>
            ),
            // Include searchable data for filtering
            searchableData: `${practice.name} ${practice.street_address} ${practice.city} ${practice.state} ${practice.zip}`.toLowerCase(),
            data: practice
          }))}
          value={practices
            .filter(practice => practice.id.toString() === jobState.practice_id?.toString())
            .map(practice => ({
              value: practice.id,
              label: (
                <div className="d-flex align-items-center">
                  <div className="location-avatar me-2">
                    {practice.avatar_url ? (
                      <img
                        src={practice.avatar_url}
                        alt={practice.name}
                        className="rounded-circle"
                        style={{ width: '32px', height: '32px', objectFit: 'cover' }}
                      />
                    ) : (
                      <div
                        className="rounded-circle bg-primary d-flex justify-content-center align-items-center"
                        style={{ width: '32px', height: '32px' }}
                      >
                        <span style={{ color: 'white', fontSize: '14px' }}>{practice.name?.charAt(0).toUpperCase()}</span>
                      </div>
                    )}
                  </div>
                  <div className="location-details">
                    <div className="location-name fw-bold">{practice.name}</div>
                    <div className="location-address" style={{ color: 'inherit', fontSize: '0.8rem' }}>
                      {practice.street_address}, {practice.city}, {practice.state}, {practice.zip}
                    </div>
                  </div>
                </div>
              ),
              searchableData: `${practice.name} ${practice.street_address} ${practice.city} ${practice.state} ${practice.zip}`.toLowerCase(),
              data: practice
            }))[0]
          }
          onChange={(selected) => updateJobState("practice_id", selected ? selected.value : "")}
          placeholder="Search or select a location"
          classNamePrefix="react-select"
          isClearable={false}
          filterOption={(option, inputValue) => {
            // Case-insensitive search on any part of the name or address
            return option.data.searchableData.toLowerCase().includes(inputValue.toLowerCase());
          }}
          noOptionsMessage={({ inputValue }) => `No locations found for "${inputValue}"`}
        />
        {locationError && (
          <p className="job-builder-field-error" role="alert">Select a location.</p>
        )}
        <div className="mt-3">
          <a className="job-builder-inline-action" href="/practices">
            <i className="fa fa-plus me-1"></i> Add Another Location
          </a>
        </div>
      </div>

      {jobState.position_type !== "temp" && (
        <div className="form-group py-2">
          <h4 className="font-weight-bold">Confidential Listing</h4>
          <p className="job-builder-field-help">
            Hide your practice&rsquo;s name, logo, and website on this listing, so candidates
            see &ldquo;Confidential Dental Practice&rdquo; instead.
          </p>
          <div className="form-check form-switch job-builder-switch">
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              checked={!!jobState.confidential}
              id="job-confidential"
              onChange={selectConfidential}
            />
            <label className="form-check-label" htmlFor="job-confidential">
              Keep practice details confidential
            </label>
          </div>
        </div>
      )}

      {autoInviteEligible && (
        <div className="form-group py-2">
          <h4 className="font-weight-bold">Automatic Invitations</h4>
          <p className="job-builder-field-help">
            Each day we invite the best-matching candidates who have not applied yet.
            Turn this off to invite people yourself.
          </p>
          <div className="form-check form-switch job-builder-switch">
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              checked={autoInviteOn}
              id="job-auto-invite-matches"
              onChange={selectAutoInvite}
            />
            <label className="form-check-label" htmlFor="job-auto-invite-matches">
              Automatically invite matching candidates
            </label>
          </div>
        </div>
      )}

      <div className={`form-group py-2${hiringTypeError ? ' has-error' : ''}`}>
        <h4 className="font-weight-bold">Hiring Type</h4>
        <p className="job-builder-field-help">Set whether this is a permanent, long-term, or single-day need.</p>
        <Select
          styles={customStyles}
          options={positionTypeOptions}
          value={selectedPositionType}
          onChange={(selected) => updateJobState("position_type", selected ? selected.value : "")}
          placeholder="Select a position type..."
          classNamePrefix="react-select"
          isClearable={false}
        />
        {hiringTypeError && (
          <p className="job-builder-field-error" role="alert">Select a hiring type.</p>
        )}
      </div>

      {jobState.position_type === "temp" && (
        <div className={`form-group py-2${smsPhoneError ? ' has-error' : ''}`}>
          <h4 className="font-weight-bold">Mobile Number</h4>
          <p className="job-builder-field-help">You will receive text message updates for your temp job request.</p>
          <input
            className="form-control"
            id="sms_phone"
            name="sms_phone"
            type="text"
            placeholder="555-555-5555"
            onChange={e => updateJobState("sms_phone", e.target.value)}
            value={jobState.sms_phone ?? ""}
          />
          {smsPhoneError && (
            <p className="job-builder-field-error" role="alert">Enter a mobile number for text updates.</p>
          )}
        </div>
      )}

      {jobState.position_type !== "temp" && (
        <div className="form-group py-2">
          <h4 className="font-weight-bold">Years of Experience</h4>
          <p className="job-builder-field-help">Minimum experience you&rsquo;d like candidates to have.</p>
          <Select
            styles={customStyles}
            options={EXPERIENCE_OPTIONS}
            value={EXPERIENCE_OPTIONS.find(o => o.value === jobState.years_experience) || null}
            onChange={(selected) => updateJobState("years_experience", selected ? selected.value : "0-2")}
            placeholder="Select experience level..."
            classNamePrefix="react-select"
            isClearable={false}
            isSearchable={false}
            inputId="years_experience"
          />
        </div>
      )}

      {jobState.position_type === "temp" && (
        <div className="form-group py-2 ">
          <h4 className="font-weight-bold">Temp Schedule</h4>
          <h5>Temp Date</h5>
          <DatePicker
            className="form-control"
            selected={jobState.temp_date}
            minDate={new Date()}
            onChange={e => tempDateHandler(e)}
          />
          {tempDateError && (
            <p className="job-builder-field-error" role="alert">Pick the date of the shift.</p>
          )}
          <div className="row">
            {" "}
            <div className="col-6">
              {" "}
              <h5>Start Time</h5>
              <Datetime
                value={
                  jobState.temp_start_readable
                    ? moment(jobState.temp_start_readable, 'h:mm A')
                    : jobState.temp_start
                    ? moment.utc(jobState.temp_start)
                    : null
                }
                inputProps={{ name: `job[temp_start]` }}
                onChange={date => startTimeHandler(date)}
                dateFormat={false}
                timeFormat={true}
                timeConstraints={{
                  minutes: {
                    step: 15,
                    min: 0,
                    max: 59
                  }
                }}
              />
              {tempStartError && (
                <p className="job-builder-field-error" role="alert">Set a start time.</p>
              )}
            </div>
            <div className="col-6">
              {" "}
              <h5>End Time</h5>
              <Datetime
                value={
                  jobState.temp_end_readable
                    ? moment(jobState.temp_end_readable, 'h:mm A')
                    : jobState.temp_end
                    ? moment.utc(jobState.temp_end)
                    : null
                }
                inputProps={{ name: `job[temp_end]` }}
                onChange={date => endTimeHandler(date)}
                dateFormat={false}
                timeFormat={true}
                timeConstraints={{
                  minutes: {
                    step: 15,
                    min: 0,
                    max: 59
                  }
                }}
              />
              {tempEndError && (
                <p className="job-builder-field-error" role="alert">Set an end time.</p>
              )}
            </div>
          </div>
        </div>
      )}
      {jobState.position_type === "longtermtemp" && (
        <div className="form-group py-2 ">
          <h4 className="font-weight-bold">Long Term Start</h4>
          <h5>Start Date</h5>
          <DatePicker
            className="form-control"
            selected={jobState.start_date}
            onChange={e => startDateHandler(e)}
          />
        </div>
      )}
      <div className="job-builder-actions">
        <button
          type="button"
          className="job-builder-button job-builder-button--secondary"
          onClick={() => window.location.href = "/jobs/manage"}
        >
          Cancel
        </button>
        <button type="button" className="job-builder-button job-builder-button--primary" onClick={() => validationGI()}>
          Continue to skills
          <i className="fa fa-arrow-right" aria-hidden="true"></i>
        </button>
      </div>
    </div>
  )
}
